import React, { useEffect, useState } from "react";
import { NavLink, useNavigate, Outlet } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useBranding } from "@/context/BrandingContext";
import api from "@/lib/api";
import { ROLE_LABEL, formatWIB } from "@/lib/format";
import { cn } from "@/lib/utils";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  LayoutDashboard, ClipboardList, Users, Inbox, Network, ScrollText, Settings,
  Bell, LogOut, Menu, Target, FileWarning, Zap, Rocket, FileText, Repeat, Users2, CalendarRange, Lightbulb, BookOpen, UserCog, FileSpreadsheet,
} from "lucide-react";
import { toast } from "sonner";

const NAV = [
  { to: "/", key: "dashboard", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/tugas-saya", key: "tugas-saya", label: "Tugas Saya", icon: ClipboardList },
  { to: "/tugas-tim", key: "tugas-tim", label: "Tugas Tim", icon: Users },
  { to: "/persetujuan", key: "persetujuan", label: "Kotak Persetujuan", icon: Inbox, badgeKey: "approvals" },
  { to: "/misi", key: "misi", label: "Misi & Strategi", icon: Rocket },
  { to: "/pengajuan", key: "pengajuan", label: "Pengajuan", icon: FileText },
  { to: "/target", key: "target", label: "Target", icon: Target },
  { to: "/rutin", key: "rutin", label: "Tugas Rutin", icon: Repeat },
  { to: "/rapat", key: "rapat", label: "Rapat", icon: Users2 },
  { to: "/bulanan", key: "bulanan", label: "Rencana & Evaluasi", icon: CalendarRange },
  { to: "/inisiatif", key: "inisiatif", label: "Program Inisiatif", icon: Lightbulb },
  { to: "/evaluasi", key: "evaluasi", label: "Evaluasi & Best Practice", icon: BookOpen },
  { to: "/form", key: "form", label: "Form & Rekap", icon: FileSpreadsheet },
  { to: "/struktur", key: "struktur", label: "Struktur Organisasi", icon: Network },
  { to: "/audit", key: "audit", label: "Audit Log", icon: ScrollText },
  { to: "/pengguna", key: "pengguna", label: "Manajemen Pengguna", icon: UserCog },
  { to: "/pengaturan", key: "pengaturan", label: "Pengaturan", icon: Settings },
];

function initials(name = "") {
  return name.split(" ").slice(0, 2).map((w) => w[0]).join("").toUpperCase();
}

function NotificationBell() {
  const [data, setData] = useState({ items: [], unread: 0 });
  const load = async () => {
    try { const r = await api.get("/notifications"); setData(r.data); } catch (e) {}
  };
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, []);
  const readAll = async () => { await api.post("/notifications/read-all"); load(); };
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button className="relative rounded-full p-2 hover:bg-slate-100 transition-colors" data-testid="notif-bell">
          <Bell className="h-5 w-5 text-slate-600" />
          {data.unread > 0 && (
            <span className="absolute -top-0.5 -right-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-rose-500 text-[10px] font-bold text-white">
              {data.unread > 9 ? "9+" : data.unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0">
        <div className="flex items-center justify-between px-4 py-2.5 border-b">
          <span className="font-semibold text-sm">Notifikasi</span>
          <button onClick={readAll} className="text-xs text-indigo-600 hover:underline" data-testid="notif-read-all">Tandai semua dibaca</button>
        </div>
        <div className="max-h-96 overflow-y-auto zoa-scroll">
          {data.items.length === 0 && <p className="p-4 text-sm text-slate-400">Belum ada notifikasi.</p>}
          {data.items.map((n) => (
            <div key={n.id} className={cn("px-4 py-2.5 border-b last:border-0", !n.is_read && "bg-indigo-50/40")}>
              <div className="flex items-start gap-2">
                <div className={cn("mt-1 h-2 w-2 rounded-full shrink-0", n.priority === "kritis" ? "bg-rose-500" : "bg-indigo-400")} />
                <div className="min-w-0">
                  <p className="text-sm font-medium text-slate-800">{n.title}</p>
                  <p className="text-xs text-slate-500">{n.body}</p>
                  <p className="text-[10px] text-slate-400 mt-0.5">{formatWIB(n.created_at)}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function SidebarContent({ user, onNav }) {
  const { branding } = useBranding();
  const feats = Array.isArray(user?.features) ? user.features : null;
  const filtered = feats ? NAV.filter((n) => feats.includes(n.key)) : NAV.filter((n) => n.key === "dashboard");
  return (
    <div className="flex h-full flex-col bg-slate-900 text-slate-100">
      <div className="flex items-center gap-2.5 px-5 py-5 border-b border-slate-800">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 shadow-lg overflow-hidden">
          {branding.logo ? <img src={branding.logo} alt="logo" className="h-10 w-10 object-contain" /> : <Zap className="h-5 w-5 text-white" fill="white" />}
        </div>
        <div>
          <p className="font-extrabold tracking-tight leading-none">{branding.app_name}</p>
          <p className="text-[11px] text-slate-400 leading-none mt-0.5">{branding.tagline}</p>
        </div>
      </div>
      <nav className="flex-1 overflow-y-auto zoa-scroll px-3 py-4 space-y-1">
        {filtered.map((n) => {
          const Icon = n.icon;
          return (
            <NavLink key={n.to} to={n.to} end={n.end} onClick={onNav} data-testid={`nav-${n.to === "/" ? "dashboard" : n.to.slice(1)}`}
              className={({ isActive }) => cn(
                "flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-all",
                isActive ? "bg-indigo-600 text-white shadow-md" : "text-slate-300 hover:bg-slate-800 hover:text-white")}>
              <Icon className="h-[18px] w-[18px]" />{n.label}
            </NavLink>
          );
        })}
      </nav>
      <div className="border-t border-slate-800 px-4 py-3">
        <div className="flex items-center gap-2.5">
          <Avatar className="h-9 w-9">
            {user?.photo && <AvatarImage src={user.photo} />}
            <AvatarFallback className="bg-indigo-500 text-white text-xs font-bold">{initials(user?.name)}</AvatarFallback>
          </Avatar>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold truncate">{user?.name}</p>
            <p className="text-[11px] text-slate-400 truncate">{ROLE_LABEL[user?.role] || user?.role}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const doLogout = async () => { await logout(); navigate("/login"); toast.success("Berhasil keluar"); };

  return (
    <div className="flex h-screen overflow-hidden bg-slate-50">
      <aside className="hidden lg:block w-64 shrink-0"><SidebarContent user={user} /></aside>
      <div className="flex flex-1 flex-col overflow-hidden">
        <header className="flex items-center justify-between gap-4 border-b border-slate-200 bg-white/80 backdrop-blur-md px-4 lg:px-6 py-3">
          <div className="flex items-center gap-3">
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild className="lg:hidden">
                <button className="rounded-lg p-2 hover:bg-slate-100" data-testid="mobile-menu-btn"><Menu className="h-5 w-5" /></button>
              </SheetTrigger>
              <SheetContent side="left" className="w-64 p-0 border-0">
                <SidebarContent user={user} onNav={() => setMobileOpen(false)} />
              </SheetContent>
            </Sheet>
            <div>
              <p className="text-xs text-slate-400 font-medium">ZOA Group · Pontianak</p>
              <p className="text-sm font-bold text-slate-800">Selamat datang, {user?.name?.split(" ")[0]} 👋</p>
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            <NotificationBell />
            <Button variant="ghost" size="sm" onClick={doLogout} data-testid="logout-btn" className="text-slate-600">
              <LogOut className="h-4 w-4 mr-1.5" /><span className="hidden sm:inline">Keluar</span>
            </Button>
          </div>
        </header>
        <main className="flex-1 overflow-y-auto zoa-scroll p-4 lg:p-6"><Outlet /></main>
      </div>
    </div>
  );
}
